import type { ReactNode } from 'react'

import { categoryDisplayName, type Tag, type TagCategory } from '@/types/api/tag'

interface CategorizedTagOptionsProps {
  tags: Tag[]
  categories: TagCategory[]
  onPick: (tag: Tag) => void
  renderTag: (tag: Tag) => ReactNode
  /** Show the "one per record" hint on single-select categories (record tags only). */
  showSingleSelectHint?: boolean
}

/** Tag picker options grouped under their category headers; uncategorized tags come last. */
export function CategorizedTagOptions({ tags, categories, onPick, renderTag, showSingleSelectHint = false }: CategorizedTagOptionsProps) {
  const known = new Set(categories.map((c) => c.id))
  const groups = [
    ...categories.map((category) => ({ category, tags: tags.filter((t) => t.categoryId === category.id) })),
    { category: null as TagCategory | null, tags: tags.filter((t) => !t.categoryId || !known.has(t.categoryId)) },
  ].filter((g) => g.tags.length > 0)

  const showHeaders = groups.some((g) => g.category)

  return (
    <>
      {groups.map(({ category, tags: groupTags }) => (
        <div key={category?.id ?? 'uncategorized'} className="space-y-0.5">
          {showHeaders && (
            <p className="px-2.5 pt-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
              {category ? categoryDisplayName(category) : 'Other'}
              {showSingleSelectHint && category?.singleSelect && (
                <span className="ml-1 font-normal normal-case tracking-normal">· replaces current</span>
              )}
            </p>
          )}
          {groupTags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              onClick={() => onPick(tag)}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left hover:bg-neutral-50"
            >
              {renderTag(tag)}
            </button>
          ))}
        </div>
      ))}
    </>
  )
}
